import { createApi } from "@reduxjs/toolkit/query/react";

import { createServiceBaseQuery } from "../../client/serviceBaseQuery";
import type { AccessResponse, BulkAccessRequest } from "../../../utils/access";
import type {
  InvocableSkill,
  SkillCategory,
  SkillCategoryOption,
  SkillCreate,
  SkillDetail,
  SkillShelfQuery,
  SkillsQuery,
  SkillSummary,
  SkillUpdate,
  SkillVersion,
  SkillVersionDetail,
} from "./types";

// fetchBaseQuery's params would join an array as one comma-separated value; the
// BFF (and zeus behind it) read a repeated key.
const toSkillsSearchParams = ({ type, owner, q, category }: SkillsQuery): string => {
  const params = new URLSearchParams();
  if (type !== undefined) params.set("type", type);
  if (owner !== undefined) params.set("owner", String(owner));
  if (q !== undefined) params.set("q", q);
  category?.forEach((id) => params.append("category", id));
  return params.toString();
};

// Skills go through nav's BFF (/api/skills), which proxies zeus and narrows the
// filter and update bodies at the boundary.
export const skillsService = createApi({
  reducerPath: "skillsService",
  baseQuery: createServiceBaseQuery("bffApiUri"),
  tagTypes: ["Skill", "SkillAccess", "SkillCategory", "SkillHistory"],
  endpoints: (builder) => ({
    getSkills: builder.query<SkillSummary[], SkillsQuery>({
      query: (query) => ({ url: `/api/skills?${toSkillsSearchParams(query)}` }),
      providesTags: [{ type: "Skill", id: "LIST" }],
    }),
    getSkillCategories: builder.query<SkillCategory[], SkillShelfQuery>({
      query: (shelf) => ({
        url: `/api/skills/categories/summary?${toSkillsSearchParams(shelf)}`,
      }),
      providesTags: [{ type: "SkillCategory", id: "LIST" }],
    }),
    // The full assignable catalogue for the tag picker. Tagged apart from the
    // counted LIST: filing a skill shifts the counts, not the set of categories,
    // so a skill edit needn't refetch this.
    getSkillCategoryCatalogue: builder.query<SkillCategoryOption[], void>({
      query: () => ({ url: "/api/skills/categories" }),
      providesTags: [{ type: "SkillCategory", id: "CATALOGUE" }],
    }),
    getSkill: builder.query<SkillDetail, string>({
      query: (skillId) => ({ url: `/api/skills/${skillId}` }),
      providesTags: (_result, _error, skillId) => [{ type: "Skill", id: skillId }],
    }),
    getSkillVersions: builder.query<SkillVersion[], string>({
      query: (skillId) => ({ url: `/api/skills/${skillId}/versions` }),
      providesTags: (_result, _error, skillId) => [{ type: "SkillHistory", id: skillId }],
    }),
    // Untagged: a past version is immutable.
    getSkillVersion: builder.query<
      SkillVersionDetail,
      { skillId: string; version: number }
    >({
      query: ({ skillId, version }) => ({
        url: `/api/skills/${skillId}/versions/${version}`,
      }),
    }),
    createSkill: builder.mutation<SkillDetail, SkillCreate>({
      query: (body) => ({ url: "/api/skills", method: "POST", body }),
      // A new skill can be the first behind a category, so the counted summary shifts.
      invalidatesTags: [
        { type: "Skill", id: "LIST" },
        { type: "SkillCategory", id: "LIST" },
      ],
      // Seed the detail cache so reopening the panel on the new skill shows it
      // straight away instead of refetching what we already hold.
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          void dispatch(skillsService.util.upsertQueryData("getSkill", data.id, data));
        } catch {
          // The create failed; the caller surfaces the error.
        }
      },
    }),
    updateSkill: builder.mutation<SkillDetail, { skillId: string; changes: SkillUpdate }>(
      {
        query: ({ skillId, changes }) => ({
          url: `/api/skills/${skillId}`,
          method: "PATCH",
          body: changes,
        }),
        // The detail isn't invalidated: the response is the saved skill and is written
        // below, so a refetch would only race it. The lists and counts hold summaries
        // of it, so those refetch.
        invalidatesTags: (_result, _error, { skillId }) => [
          { type: "Skill", id: "LIST" },
          { type: "SkillCategory", id: "LIST" },
          { type: "SkillHistory", id: skillId },
        ],
        // Pessimistic update: updateQueryData applies synchronously (upsertQueryData
        // runs as a thunk and lands later), so the entry is current as soon as the
        // save settles.
        async onQueryStarted({ skillId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(skillsService.util.updateQueryData("getSkill", skillId, () => data));
          } catch {
            // The update failed; the caller surfaces the error.
          }
        },
      },
    ),
    deleteSkill: builder.mutation<void, string>({
      query: (skillId) => ({ url: `/api/skills/${skillId}`, method: "DELETE" }),
      async onQueryStarted(skillId, lifecycle) {
        try {
          await lifecycle.queryFulfilled;
        } catch {
          return;
        }
        // The skill can sit on more than one shelf and under more than one category
        // filter, and each is its own cache entry.
        skillsService.util
          .selectCachedArgsForQuery(lifecycle.getState(), "getSkills")
          .forEach((query) => {
            lifecycle.dispatch(
              skillsService.util.updateQueryData("getSkills", query, (draft) => {
                const index = draft.findIndex((skill) => skill.id === skillId);
                if (index !== -1) draft.splice(index, 1);
              }),
            );
          });
        // The summary doesn't say which categories it counted towards.
        lifecycle.dispatch(
          skillsService.util.invalidateTags([{ type: "SkillCategory", id: "LIST" }]),
        );
      },
    }),
    getSkillAccess: builder.query<AccessResponse, string>({
      query: (skillId) => ({ url: `/api/skills/${skillId}/access` }),
      providesTags: (_result, _error, skillId) => [{ type: "SkillAccess", id: skillId }],
    }),
    updateSkillAccess: builder.mutation<
      void,
      { skillId: string; request: BulkAccessRequest }
    >({
      query: ({ skillId, request }) => ({
        url: `/api/skills/${skillId}/access`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, { skillId }) => [
        { type: "SkillAccess", id: skillId },
      ],
    }),
    getInvocableSkills: builder.query<InvocableSkill[], void>({
      query: () => ({ url: "/api/skills/invocable" }),
      providesTags: [{ type: "Skill", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSkillsQuery,
  useGetSkillCategoriesQuery,
  useGetSkillCategoryCatalogueQuery,
  useGetSkillQuery,
  useGetSkillVersionsQuery,
  useGetSkillVersionQuery,
  useGetInvocableSkillsQuery,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useGetSkillAccessQuery,
  useUpdateSkillAccessMutation,
} = skillsService;
